import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useMemo, useState } from 'react';
import jwtDecode from 'jwt-decode';

export type Permission =
  | 'order:read'
  | 'order:create'
  | 'order:update'
  | 'set:read'
  | 'tray:read'
  | 'part:read'
  | 'user:create'
  | 'savi_user:create'
  | 'location:read'
  | 'db:correction'
  | 'log:read'
  | 'log:list'
  | 'part:read'
  | 'part:create'
  | 'part_draft:read'
  | 'part_draft:create'
  | 'part_draft:update'
  | 'part_draft:merge'
  | 'draft:read'
  | 'draft:create'
  | 'draft:update'
  | 'draft:approve'
  | 'set:change_status'
  | 'set_draft:read'
  | 'set_draft:list'
  | 'set_draft:create'
  | 'set_draft:merge'
  | 'set_draft:delete'
  | 'report:generate'
  | 'set_draft:update'
  | 'set_draft:approve'
  | 'user_token:create'
  | 'savi_user:create'
  | 'savi_user:read'
  | 'savi_user:delete'
  | 'savi_user:update'
  | 'tenant:read'
  | 'user_migration:run'
  | 'order:create'
  | 'model_config:read'
  | 'model_config:write'
  | 'bulk_upload:use'
  | 'connect:use'
  | 'hospital_order:read'
  | 'hospital_order:create'
  | 'hospital_order:update'
  | 'suppliers:read'
  | 'surgeons:read'
  | 'surgeons:write'
  | 'suppliers:create'
  | 'suppliers:update';

export type UseUserHasPermissionsProps = {
  permissions?: Permission[];
  some?: boolean;
};

export type UserPermissions = Permission[] | null;

export type UseUserHasPermissions = {
  hasPermissions: boolean;
  userPermissions: UserPermissions;
  isLoading: boolean;
};

const useUserHasPermissions = ({ permissions, some = false }: UseUserHasPermissionsProps): UseUserHasPermissions => {
  const [userPermissions, setUserPermissions] = useState<UserPermissions>(null);
  const isLoading = !userPermissions;

  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    (async () => {
      const token = await getAccessTokenSilently();
      if (!token) return;

      const userProfile: { permissions?: Permission[] } = jwtDecode(token);
      setUserPermissions(userProfile.permissions || []);
    })();
  }, [getAccessTokenSilently]);

  const hasPermissions = useMemo<boolean>(() => {
    if (!permissions?.length) return true;
    if (isLoading) return false;
    const method = some ? 'some' : 'every';
    return permissions[method](permission => userPermissions.includes(permission));
  }, [isLoading, permissions, some, userPermissions]);

  return {
    hasPermissions,
    userPermissions,
    isLoading,
  };
};

export default useUserHasPermissions;
